import { type ReactNode, type Ref } from 'react'
import cx from 'classnames'
import { GridList, GridListItem } from 'react-aria-components'
import { Icon, type IconName } from '@/components/Icon'
import { useSkeletonListContext } from '@/components/Skeleton/SkeletonList'
import { Skeleton } from '@/components/Skeleton'
import styles from './AcceptanceList.module.css'

export interface AcceptanceListProps {
  children?: ReactNode
  'aria-labelledby'?: string
  testId?: string
}

export function AcceptanceList({
  children,
  testId,
  ...rest
}: AcceptanceListProps) {
  return (
    <GridList {...rest} className={styles.acceptanceList} data-test={testId}>
      {children}
    </GridList>
  )
}

export interface AcceptanceListItemProps {
  icon?: IconName
  title: ReactNode
  subtitle: ReactNode
  textValue: string
  onAction?: () => unknown
  testId?: string
}

export function AcceptanceListItem({
  icon = 'file',
  title,
  subtitle,
  textValue,
  onAction,
  testId,
}: AcceptanceListItemProps) {
  return (
    <GridListItem
      onAction={onAction}
      textValue={textValue}
      data-test={testId}
      className={({ isHovered, isFocusVisible }) =>
        cx(
          styles.acceptanceItem,
          isHovered && styles.hover,
          isFocusVisible && styles.focusVisible,
        )
      }
    >
      <div className={styles.icon}>
        <Icon name={icon} variant="inherit" />
      </div>
      <div className={styles.agreementInfo}>
        <p className={styles.agreementTitle}>{title}</p>
        <p className={styles.agreementSubtitle}>{subtitle}</p>
      </div>
      <div className={styles.chevron}>
        <Icon name="chevron" variant="secondary" />
      </div>
    </GridListItem>
  )
}

export function AcceptanceListItemSkeleton() {
  const { varyPercent, ref } = useSkeletonListContext()
  return (
    <GridListItem
      isDisabled
      textValue="Placeholder"
      className={cx(styles.acceptanceItem, styles.skeleton)}
      ref={ref as Ref<HTMLDivElement>}
    >
      <div className={styles.icon}>
        <Icon name="file" skeleton />
      </div>
      <div className={styles.agreementInfo}>
        <p className={styles.agreementTitle}>
          <Skeleton width={varyPercent(50)} />
        </p>
        <p className={styles.agreementSubtitle}>
          <Skeleton width={varyPercent(40)} />
        </p>
      </div>
    </GridListItem>
  )
}
